import { type ReactNode, useMemo } from 'react';
import { type Control, Controller, type FieldValues } from 'react-hook-form';
import { useIntl } from 'react-intl';
import {
  type CountryCode,
  DEFAULT_COUNTRY_CODES,
} from '@repo/design-system-kit/constants/countries';
import { Flag } from '@repo/design-system-kit/components/flag/flag';
import { MultiSelect } from '@repo/design-system-kit/components/multi-select/multi-select';
import type { Key } from 'react-aria';
import type { PathOfType } from '../../types/path-of-type';
import styles from './country-multi-select.module.css';

interface CountryOption {
  key: string;
  value: string;
  beforeContent: ReactNode;
  recommended?: boolean;
}

interface CountryMultiSelectFieldProps<T extends FieldValues, TContext, TTransformedValues = T> {
  control: Control<T, TContext, TTransformedValues>;
  name: PathOfType<T, string[]>;
  label: string;
  blacklistedCountries?: CountryCode[];
  className?: string;
  description?: string;
  recommendedCountries?: CountryCode[];
  placeholder?: string;
  errorMessage?: string;
  isRequired?: boolean;
}

export function CountryMultiSelectField<T extends FieldValues, TContext, TTransformedValues = T>({
  control,
  name,
  label,
  blacklistedCountries = [],
  className,
  description,
  recommendedCountries = [],
  placeholder,
  errorMessage,
  isRequired = false,
}: CountryMultiSelectFieldProps<T, TContext, TTransformedValues>): ReactNode {
  const { formatMessage, formatDisplayName } = useIntl();

  const countryOptions = useMemo<CountryOption[]>(() => {
    return DEFAULT_COUNTRY_CODES.filter(code => !blacklistedCountries.includes(code)).map(code => ({
      key: code,
      value: formatDisplayName(code, { type: 'region' }) ?? code,
      beforeContent: <Flag code={code} />,
      recommended: recommendedCountries.includes(code),
    }));
  }, [formatDisplayName, recommendedCountries, blacklistedCountries]);

  const defaultPlaceholder = useMemo(() => {
    if (placeholder) return placeholder;
    return countryOptions.filter(option => option.recommended).length > 0
      ? countryOptions.filter(option => option.recommended)[0]?.value || ''
      : countryOptions[0]?.value || '';
  }, [countryOptions, placeholder]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <MultiSelect
          description={fieldState.invalid ? undefined : description}
          errorMessage={fieldState.error?.message || errorMessage}
          isInvalid={fieldState.invalid}
          items={countryOptions}
          label={label}
          onSelectionChange={(keys: Set<Key>) => {
            const selectedArray = Array.from(keys).map(key => String(key));
            field.onChange(selectedArray);
          }}
          placeholder={defaultPlaceholder}
          renderOption={({ beforeContent, value, recommended, key }) => (
            <span
              className={styles.option}
              data-recommended={recommended}
              data-testid={`country-multi-select-option-${key}`}
            >
              <span>{beforeContent}</span>
              {value}
            </span>
          )}
          selectedKeys={new Set(Array.isArray(field.value) ? field.value : [])}
          className={className}
          withFooter={false}
          withSearch={{
            placeholder: formatMessage({
              id: 'customer-registration-kit.list-select.search.placeholder',
            }),
            emptyMessage: formatMessage({
              id: 'generic.no-results.error',
            }),
          }}
        />
      )}
      rules={{
        validate: value => {
          if (isRequired && (!Array.isArray(value) || value.length === 0)) {
            return errorMessage || formatMessage({ id: 'generic.required-field.error' });
          }
          return true;
        },
      }}
    />
  );
}
